<script setup lang="ts">
  import { computed, ref, useTemplateRef, watch } from 'vue'
  import { IconEdit } from '@gat-ship/icons'
  import {
    Button,
    FormSubmit,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    UnderlineTab,
    UnderlineTabs
  } from '@gat-ship/vue-components'
  import type { AiPromptAsset, PromptTarget } from '../model/types'
  import { useIntrospect } from '../composables/useIntrospect'
  import AiPresetForm from './AiPresetForm.vue'

  const props = defineProps<{
    asset?: AiPromptAsset
    target: AiPromptAsset[] | PromptTarget
    allAssets?: AiPromptAsset[]
    virtualAssets?: AiPromptAsset[]
    targetId?: PromptTarget
  }>()

  const isOpen = defineModel<boolean>('open', { required: true })

  const emit = defineEmits<{
    (e: 'update', asset: AiPromptAsset): void
    (e: 'remove', asset: AiPromptAsset): void
  }>()

  type Tab = 'details' | 'properties' | 'base-instructions'
  const selectedTab = ref<Tab>('details')

  const resolvedTarget = computed((): PromptTarget => {
    if (typeof props.target === 'string') return props.target as PromptTarget
    const target = props.asset?.promptTargetId ?? props.targetId
    if (!target) {
      throw new Error('Prompt preset target is required')
    }
    return target
  })

  const {
    detailLevel,
    renderedProperties,
    renderedBaseInstructions,
    extractionAiModel,
    refetch
  } = useIntrospect({ target: resolvedTarget, fetchOnMount: false })

  const baseInstructionAiModel = computed(() => extractionAiModel.value)
  const formRef = useTemplateRef<InstanceType<typeof AiPresetForm>>('form')

  const hasFetchedIntrospect = ref(false)
  watch(selectedTab, (tab) => {
    if (
      !hasFetchedIntrospect.value &&
      (tab === 'properties' || tab === 'base-instructions')
    ) {
      hasFetchedIntrospect.value = true
      refetch()
    }
  })
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <modal v-model:open="isOpen" type="drawer" class="min-w-[600px]">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconEdit class="lucide-icon size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Edit Prompt Preset
      </ModalHeaderTitle>
    </ModalHeader>
    <UnderlineTabs v-model="selectedTab" class="px-5">
      <UnderlineTab value="details">Details</UnderlineTab>
      <UnderlineTab value="properties">Properties</UnderlineTab>
      <UnderlineTab value="base-instructions"> Base Instructions </UnderlineTab>
    </UnderlineTabs>

    <ModalContent v-if="isOpen && asset">
      <ModalSection>
        <div v-show="selectedTab === 'details'">
          <AiPresetForm
            ref="form"
            :key="asset.id"
            :asset="asset"
            command="update"
            :base-instruction-ai-model="baseInstructionAiModel"
            :all-assets="allAssets"
            :virtual-assets="virtualAssets"
            :target="targetId"
            @save="emit('update', $event)"
            @remove="emit('remove', $event)"
          />
        </div>

        <div v-show="selectedTab === 'properties'" class="flex flex-col gap-3">
          <div class="flex items-center gap-1 text-xs">
            <span class="text-muted-foreground mr-1">Detail:</span>
            <button
              type="button"
              class="rounded px-2 py-0.5 transition-colors"
              :class="
                detailLevel === 'simple'
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted/40'
              "
              @click="detailLevel = 'simple'"
            >
              Simple
            </button>
            <button
              type="button"
              class="rounded px-2 py-0.5 transition-colors"
              :class="
                detailLevel === 'extended'
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted/40'
              "
              @click="detailLevel = 'extended'"
            >
              Extended
            </button>
          </div>
          <div
            class="prose prose-sm prose-neutral max-h-[60vh] max-w-none overflow-y-auto dark:prose-invert prose-h1:text-lg"
            v-html="renderedProperties"
          />
        </div>

        <div v-show="selectedTab === 'base-instructions'">
          <div
            class="prose prose-sm prose-neutral max-h-[60vh] max-w-none overflow-y-auto dark:prose-invert prose-h1:text-lg"
            v-html="renderedBaseInstructions"
          />
        </div>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <Button
        v-if="asset && formRef?.canRemove"
        variant="ghost"
        @click="emit('remove', asset)"
      >
        {{ formRef?.isSystemPrompt ? 'Reset' : 'Remove' }}
      </Button>
      <Button class="ml-auto" variant="ghost" @click="isOpen = false">
        Cancel
      </Button>
      <FormSubmit v-if="formRef?.form" :form="formRef.form" label="Save" />
    </ModalFooter>
  </modal>
</template>
